/**
 * Upsell Popup JavaScript
 * Handles a template-scoped collection upsell with multi-select products.
 */

(function() {
  'use strict';

  class UpsellPopup {
    constructor() {
      this.config = { enabled: false, products: [] };
      this.overlay = document.getElementById('upsellPopupOverlay');
      if (!this.overlay) return;

      this.popup = this.overlay.querySelector('.upsell-popup');
      this.configEl = document.getElementById('upsell-popup-config');
      if (this.configEl) {
        try {
          this.config = JSON.parse(this.configEl.textContent);
        } catch (e) {
          console.error('[UpsellPopup] Failed to parse config:', e);
        }
      }

      this.selectedProducts = {};
      this.mainProductFormData = null;
      this.mainProductForm = null;
      this.activeDrawer = null;
      this.activePopover = null;
      this.isSubmitting = false;
      this.lockedScrollY = null;
      this.variantDataCache = new Map();

      this.clearStaleScrollLock();
      this.init();
    }

    init() {
      if (!this.config.enabled || this.config.products.length === 0) return;

      this.bindEvents();
    }

    bindEvents() {
      const closeBtn = this.overlay.querySelector('[data-upsell-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.onCloseClick();
        });
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.onCloseClick();
        }
      });

      this.overlay.querySelectorAll('[data-upsell-product]').forEach((item) => {
        item.addEventListener('click', () => this.toggleProduct(item));
      });

      this.overlay.querySelector('[data-upsell-yes]')?.addEventListener('click', (e) => {
        e.preventDefault();
        this.submitSelection();
      });

      this.overlay.querySelector('[data-upsell-no]')?.addEventListener('click', (e) => {
        e.preventDefault();
        this.submitWithoutUpsell();
      });

      // Completion popup buttons
      const continueBtn = this.overlay.querySelector('[data-upsell-continue]');
      if (continueBtn) {
        continueBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.onContinueShopping();
        });
      }

      const checkoutBtn = this.overlay.querySelector('[data-upsell-checkout]');
      if (checkoutBtn) {
        checkoutBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.onGoToCheckout();
        });
      }
    }

    initFormInterception(container = document) {
      if (!this.isEnabled()) return;

      const productForms = [];
      if (container.matches?.('form[is="product-form"], form[id*="product-form"]')) {
        productForms.push(container);
      }
      productForms.push(...container.querySelectorAll('form[is="product-form"], form[id*="product-form"]'));
      productForms.forEach((productForm) => {
        if (!this.isEligibleForm(productForm)) return;
        if (productForm.dataset.upsellInitialized === 'true') return;
        productForm.dataset.upsellInitialized = 'true';

        const requiresImageUpload = productForm.dataset.upsellRequiresImageUpload !== 'false';
        if (requiresImageUpload) return;

        productForm.addEventListener('submit', (event) => {
          if (!this.isEnabled()) return;

          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          this.openForForm(productForm);
          return false;
        }, true);
      });
    }

    observeDynamicForms() {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;

            if (node.matches?.('form[is="product-form"], form[id*="product-form"], quick-buy-drawer, quick-buy-popover, .drawer__content, .popover__content')) {
              setTimeout(() => this.initFormInterception(node), 100);
            } else if (node.querySelector?.('form[is="product-form"], form[id*="product-form"]')) {
              setTimeout(() => this.initFormInterception(node), 100);
            }
          });
        });
      });

      observer.observe(document.body, { childList: true, subtree: true });
      this.formObserver = observer;
    }

    isEnabled() {
      return this.config.enabled && this.config.products.length > 0;
    }

    isOpen() {
      return this.overlay && this.overlay.classList.contains('active');
    }

    isEligibleForm(productForm) {
      if (!productForm || !productForm.isConnected) return false;

      return !productForm.closest(
        'quick-buy-drawer, .drawer--quick-buy'
      );
    }

    async resolveSelectedVariantId(productForm) {
      const formContainer = productForm.closest(
        '.product-form, .quick-buy-product, quick-buy-drawer, quick-buy-popover, .drawer__content, .popover__content'
      );
      const variantSelector = formContainer?.querySelector('.product-form__variants');
      const productHandle = variantSelector?.getAttribute('handle');
      const selectedOptions = variantSelector
        ? Array.from(variantSelector.querySelectorAll('input[name^="option"]:checked, select[name^="option"]'))
          .map((input) => input.value)
        : [];

      if (!productHandle || selectedOptions.length === 0) return null;

      try {
        let productData = this.variantDataCache.get(productHandle);
        if (!productData) {
          const response = await fetch(`/products/${encodeURIComponent(productHandle)}.js`, {
            credentials: 'same-origin'
          });
          if (!response.ok) return null;

          productData = await response.json();
          this.variantDataCache.set(productHandle, productData);
        }

        const variant = productData.variants?.find((candidate) =>
          candidate.options?.every((option, index) => option === selectedOptions[index])
        );
        return variant?.id ? String(variant.id) : null;
      } catch (error) {
        console.warn('[UpsellPopup] Unable to resolve the selected variant:', error);
        return null;
      }
    }

    async serializeForm(productForm) {
      if (!this.isEligibleForm(productForm)) return null;

      const formData = new FormData(productForm);
      const selectedVariantId = await this.resolveSelectedVariantId(productForm);
      if (selectedVariantId) formData.set('id', selectedVariantId);
      formData.delete('option1');
      formData.delete('option2');
      formData.delete('option3');
      return formData;
    }

    async openForForm(productForm) {
      if (!this.isEnabled() || !this.isEligibleForm(productForm)) return false;

      const formData = await this.serializeForm(productForm);
      if (!formData) return false;

      this.open(formData, productForm);
      return true;
    }

    open(mainFormData, mainForm) {
      this.mainProductFormData = mainFormData;
      this.mainProductForm = mainForm;
      this.isSubmitting = false;

      // Store reference to drawer/popover for later
      const drawer = document.querySelector('quick-buy-drawer[open]');
      const popover = document.querySelector('quick-buy-popover[open]');
      if (drawer) {
        this.activeDrawer = drawer;
        // Add upsell popup to focus trap's container elements
        if (drawer._focusTrap && drawer._focusTrap.updateContainerElements) {
          drawer._focusTrap.updateContainerElements([drawer, this.overlay]);
        }
      }
      if (popover) {
        this.activePopover = popover;
        // Add upsell popup to focus trap's container elements
        if (popover._focusTrap && popover._focusTrap.updateContainerElements) {
          popover._focusTrap.updateContainerElements([popover, this.overlay]);
        }
      }

      this.resetSelectionState();
      this.overlay.classList.add('active');
      this.lockPageScroll();
    }

    close() {
      this.overlay.classList.remove('active');
      this.unlockPageScroll();
      this.isSubmitting = false;

      // Remove upsell popup from focus trap's container elements
      if (this.activeDrawer && this.activeDrawer._focusTrap && this.activeDrawer._focusTrap.updateContainerElements) {
        this.activeDrawer._focusTrap.updateContainerElements([this.activeDrawer]);
      }
      if (this.activePopover && this.activePopover._focusTrap && this.activePopover._focusTrap.updateContainerElements) {
        this.activePopover._focusTrap.updateContainerElements([this.activePopover]);
      }

      // Clear references
      this.activeDrawer = null;
      this.activePopover = null;

      this.resetPopup();
    }

    lockPageScroll() {
      if (this.lockedScrollY !== null) return;

      const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      this.lockedScrollY = scrollY;
      document.documentElement.style.setProperty('--upsell-popup-scroll-y', `-${scrollY}px`);
      document.documentElement.classList.add('upsell-popup-scroll-lock');
      document.body.classList.add('upsell-popup-scroll-lock');
    }

    clearStaleScrollLock() {
      if (this.overlay.classList.contains('active')) return;
      document.documentElement.classList.remove('upsell-popup-scroll-lock');
      document.body.classList.remove('upsell-popup-scroll-lock');
      document.documentElement.style.removeProperty('--upsell-popup-scroll-y');
    }

    unlockPageScroll() {
      if (this.lockedScrollY === null) return;

      const scrollY = this.lockedScrollY;
      document.documentElement.classList.remove('upsell-popup-scroll-lock');
      document.body.classList.remove('upsell-popup-scroll-lock');
      document.documentElement.style.removeProperty('--upsell-popup-scroll-y');
      this.lockedScrollY = null;
      window.scrollTo(0, scrollY);
    }

    onCloseClick() {
      this.close();
    }

    resetPopup() {
      this.selectedProducts = {};
      this.mainProductFormData = null;
      this.mainProductForm = null;
      this.hideLoading();
      this.hideCompletion();
    }

    resetSelectionState() {
      this.selectedProducts = {};
      this.overlay.querySelectorAll('[data-upsell-product]').forEach((item) => {
        const selected = item.dataset.defaultSelected === 'true';
        this.setProductSelected(item, selected);
      });
    }

    toggleProduct(item) {
      this.setProductSelected(item, item.getAttribute('aria-pressed') !== 'true');
    }

    setProductSelected(item, selected) {
      const variantId = item.dataset.variantId;
      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-pressed', String(selected));

      if (selected && variantId) {
        this.selectedProducts[variantId] = { variantId, quantity: 1, properties: {} };
      } else if (variantId) {
        delete this.selectedProducts[variantId];
      }
    }

    submitSelection() {
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      this.complete();
    }

    submitWithoutUpsell() {
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      this.selectedProducts = {};
      this.complete();
    }

    async complete() {
      const cartAddUrl = window.themeVariables?.routes?.cartAddUrl || '/cart/add';

      // Show loading state
      this.showLoading();

      try {
        // Re-snapshot the live product form so late-arriving hidden inputs
        // (Generated Image is written after the user picks a preview) are included.
        if (this.mainProductForm && this.mainProductForm.isConnected) {
          const freshFormData = await this.serializeForm(this.mainProductForm);
          if (freshFormData) this.mainProductFormData = freshFormData;
        }

        const mainProductId = this.mainProductFormData?.get('id');
        if (!this.mainProductFormData || !mainProductId) {
          console.error('[UpsellPopup] Main product data is unavailable, aborting add to cart', {
            hasForm: Boolean(this.mainProductForm),
            formConnected: Boolean(this.mainProductForm?.isConnected),
            hasFormData: Boolean(this.mainProductFormData),
          });
          this.hideLoading();
          this.close();
          alert('Unable to add the main product. Please try again.');
          return;
        }

        // Collect all items to add in a single batch request
        const itemsToAdd = [];

        // 1. Add main product
        if (this.mainProductFormData) {
          const mainProductQuantity = this.mainProductFormData.get('quantity') || 1;

          if (mainProductId) {
            const mainItem = {
              id: parseInt(mainProductId),
              quantity: parseInt(mainProductQuantity)
            };

            // Collect properties from main form
            const properties = {};
            for (const [key, value] of this.mainProductFormData.entries()) {
              if (key.startsWith('properties[') && value) {
                const propName = key.replace('properties[', '').replace(']', '');
                properties[propName] = value;
              }
            }

            // Fallback: pull Generated Image / _Display Image / _JobID directly from
            // the live hidden inputs when the form snapshot missed them (e.g. hidden
            // input value was written after the popup opened).
            const mainFormId = this.mainProductForm?.getAttribute?.('id') || '';
            const readLiveHiddenValue = (id) => {
              const scoped = this.mainProductForm?.querySelector?.(`[id="${id}"]`);
              if (scoped && scoped.value) return scoped.value.trim();
              if (!mainFormId) return '';

              const linked = Array.from(document.querySelectorAll(`[id="${id}"]`))
                .find((input) => input.getAttribute('form') === mainFormId);
              return linked && linked.value ? linked.value.trim() : '';
            };

            const hiddenPairs = [
              ['Generated Image', 'ai-custom-image-url'],
              ['_Display Image', 'ai-custom-image-url-display'],
              ['_JobID', 'ai-custom-image-url-job-id'],
              ['Generated Image 2', 'ai-custom-image-url-2'],
              ['_Display Image 2', 'ai-custom-image-url-2-display'],
              ['_JobID 2', 'ai-custom-image-url-2-job-id'],
            ];
            hiddenPairs.forEach(([propName, elementId]) => {
              if (!properties[propName]) {
                const liveValue = readLiveHiddenValue(elementId);
                if (liveValue) properties[propName] = liveValue;
              }
            });

            if (Object.keys(properties).length > 0) {
              mainItem.properties = properties;
            }

            const requiresImageUpload =
              this.mainProductForm?.dataset.upsellRequiresImageUpload !== 'false';
            const formScope = this.mainProductForm?.closest(
              '.product-form, .quick-buy-product, quick-buy-drawer, quick-buy-popover, .drawer__content, .popover__content'
            );
            const secondUploadContainer = formScope?.querySelector(
              '.ai-upload-container[data-upload-index="2"]'
            );
            const secondUploadColumn = secondUploadContainer?.closest('.ai-upload-col');
            const requiresSecondImage = Boolean(
              secondUploadContainer &&
              window.getComputedStyle(secondUploadContainer).display !== 'none' &&
              (!secondUploadColumn || window.getComputedStyle(secondUploadColumn).display !== 'none')
            );
            const missingImageIndex = requiresImageUpload && !properties['Generated Image']
              ? 1
              : requiresImageUpload && requiresSecondImage && !properties['Generated Image 2']
                ? 2
                : 0;

            if (missingImageIndex) {
              console.error(`[UpsellPopup] Missing Generated Image${missingImageIndex === 2 ? ' 2' : ''}, aborting add to cart`, {
                productId: mainProductId,
                formId: mainFormId,
                requiresSecondImage,
                capturedKeys: Array.from(this.mainProductFormData.keys()),
              });
              this.hideLoading();
              this.close();
              alert(`Please upload ${missingImageIndex === 2 ? 'the second photo' : 'your photo'} before continuing.`);

              const uploadContainer =
                formScope?.querySelector(
                  `.ai-upload-container${missingImageIndex === 2 ? '[data-upload-index="2"]' : ':not([data-upload-index="2"])'}`
                );
              uploadContainer?.scrollIntoView({ behavior: 'smooth', block: 'center' });

              const trigger = uploadContainer?.querySelector(
                missingImageIndex === 2 ? '#ai-upload-trigger-2' : '#ai-upload-trigger'
              );
              if (trigger) trigger.classList.add('validation-error');
              return;
            }

            itemsToAdd.push(mainItem);
          }
        }

        // 2. Collect checked accessories
        const jquery = window.jQuery;
        if (jquery) {
          const allCheckedInputs = jquery('.accessories-item input[data-action="bundle-add-to-cart"]:checked');
          allCheckedInputs.each(function(index, ele) {
            const itemId = jquery(ele).attr('data-product-variant-id');
            const itemQuantity = parseInt(jquery(ele).attr('data-product-qty')) || 1;
            if (itemId) {
              itemsToAdd.push({
                id: parseInt(itemId),
                quantity: itemQuantity
              });
            }
          });
        }

        // 3. Collect upsell products
        for (const product of Object.values(this.selectedProducts)) {
          const upsellItem = {
            id: parseInt(product.variantId),
            quantity: product.quantity || 1
          };
          if (Object.keys(product.properties).length > 0) {
            upsellItem.properties = product.properties;
          }
          itemsToAdd.push(upsellItem);
        }


        // Add all items in a single batch request
        if (itemsToAdd.length > 0) {
          const mainItem = itemsToAdd[0];
          const cartSnapshot = mainItem?.properties?.['Generated Image']
            ? await window.GeneratedImageCartGuard.snapshot()
            : null;
          const response = await fetch(`${cartAddUrl}.js`, {
            body: JSON.stringify({ items: itemsToAdd }),
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            credentials: 'same-origin'
          });

          if (!response.ok) {
            throw new Error(`Cart add failed: ${response.status}`);
          }
          let responseData;
          try {
            responseData = await response.json();
          } catch (parseError) {
            if (cartSnapshot) {
              await window.GeneratedImageCartGuard.verify(null, mainItem, cartSnapshot);
            }
            throw parseError;
          }
          if (cartSnapshot) {
            await window.GeneratedImageCartGuard.verify(responseData, mainItem, cartSnapshot);
          }
        }

        // Refresh the cart drawer once at the end
        await this.refreshCart();

        // Hide loading, show completion popup
        this.hideLoading();
        this.showCompletion();

        // Clear selected images
        this.clearSelectedImages();

      } catch (error) {
        console.error('[UpsellPopup] Error adding products to cart:', error);
        this.hideLoading();
        this.close();
        alert('Unable to add the product safely. Please try again.');
      }
    }

    /**
     * Add checked accessories to cart (kept for backwards compatibility, but not used in batch mode)
     * These are the "Additional Customization Options" checkboxes
     */
    async addAccessoriesToCart(cartAddUrl) {
      const jquery = window.jQuery;
      if (!jquery) {
        return;
      }

      const allCheckedInputs = jquery('.accessories-item input[data-action="bundle-add-to-cart"]:checked');
      const inputCount = allCheckedInputs.length;


      if (inputCount === 0) {
        return;
      }

      const accessoriesForm = new FormData();
      allCheckedInputs.each(function(index, ele) {
        const itemId = jquery(ele).attr('data-product-variant-id');
        const itemQuantity = parseInt(jquery(ele).attr('data-product-qty')) || 1;
        accessoriesForm.set(`items[${index}]id`, itemId);
        accessoriesForm.set(`items[${index}]quantity`, itemQuantity);
      });

      await fetch(`${cartAddUrl}.js`, {
        body: accessoriesForm,
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        credentials: 'same-origin'
      });

    }

    async refreshCart() {
      try {
        // Fetch cart with sections for proper drawer refresh
        const cartUrl = window.themeVariables?.routes?.cartUrl || '/cart';
        const response = await fetch(`${cartUrl}.js?sections=mini-cart`, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin'
        });
        const cartContent = await response.json();


        // Dispatch cart:updated event
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true,
            detail: {
              cart: cartContent,
            },
          })
        );

        // Dispatch cart:refresh event to refresh the cart drawer
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: {
              cart: cartContent,
              openMiniCart: false, // Don't auto-open the cart drawer
            },
          })
        );

      } catch (error) {
        console.error('[UpsellPopup] Error refreshing cart:', error);
      }
    }

    showLoading() {
      const loadingEl = this.overlay.querySelector('[data-upsell-loading]');
      if (loadingEl) {
        loadingEl.classList.add('active');
      }
    }

    hideLoading() {
      const loadingEl = this.overlay.querySelector('[data-upsell-loading]');
      if (loadingEl) {
        loadingEl.classList.remove('active');
      }
    }

    showSuccess() {
      const successEl = this.overlay.querySelector('[data-upsell-success]');
      if (successEl) {
        successEl.classList.add('active');
      }
    }

    hideSuccess() {
      const successEl = this.overlay.querySelector('[data-upsell-success]');
      if (successEl) {
        successEl.classList.remove('active');
      }
    }

    showCompletion() {
      const completionEl = this.overlay.querySelector('[data-upsell-completion]');
      if (completionEl) {
        completionEl.classList.add('active');
      }
    }

    hideCompletion() {
      const completionEl = this.overlay.querySelector('[data-upsell-completion]');
      if (completionEl) {
        completionEl.classList.remove('active');
      }
    }

    trackCompletionButtonClick(eventName) {
      if (!window.GtagUtils || typeof window.GtagUtils.sendEvent !== 'function') return;

      const productInfo = typeof window.GtagUtils.getProductInfo === 'function'
        ? window.GtagUtils.getProductInfo()
        : {};

      window.GtagUtils.sendEvent(eventName, {
        product_id: productInfo.product_id || '',
        product_variant_id: String(this.mainProductFormData?.get('id') || ''),
        upsell_item_count: Object.keys(this.selectedProducts).length,
        section_id: this.config.sectionId || '',
        page_path: window.location.pathname
      });
    }

    onContinueShopping() {
      this.trackCompletionButtonClick('upsell_continue_shopping_click');
      this.hideCompletion();
      this.close();
    }

    onGoToCheckout() {
      this.trackCompletionButtonClick('upsell_checkout_click');
      this.hideCompletion();
      this.close();
      this.goToCheckout();
    }

    clearSelectedImages() {

      // Helper function to clear upload components in a specific context
      const clearInContext = (context, contextName) => {

        // Clear hidden input fields
        const hiddenInput1 = context.querySelector('#ai-custom-image-url');
        const hiddenInput2 = context.querySelector('#ai-custom-image-url-2');
        const hiddenInputJob1 = context.querySelector('#ai-custom-image-url-job-id');
        const hiddenInputJob2 = context.querySelector('#ai-custom-image-url-2-job-id');

        if (hiddenInput1) {
          hiddenInput1.value = '';
        }
        if (hiddenInput2) {
          hiddenInput2.value = '';
        }
        if (hiddenInputJob1) hiddenInputJob1.value = '';
        if (hiddenInputJob2) hiddenInputJob2.value = '';

        // Clear by name patterns
        const imageInputs = context.querySelectorAll(
          'input[name="properties[Generated Image]"], ' +
          'input[name="properties[Generated Image 2]"], ' +
          'input[name="properties[Custom Image]"], ' +
          'input[name="properties[_Custom Image]"], ' +
          'input[name="properties[Custom Image 2]"], ' +
          'input[name="properties[_Custom Image 2]"], ' +
          'input[name="properties[_JobID]"], ' +
          'input[name="properties[_JobID 2]"]'
        );
        imageInputs.forEach(input => {
          input.value = '';
        });

        // Clear first upload preview
        const previewImg1 = context.querySelector('#ai-upload-preview');
        const uploadBtn1 = context.querySelector('#ai-upload-trigger');
        const statusDiv1 = context.querySelector('#ai-upload-status');

        if (previewImg1) {
          previewImg1.src = '';
          previewImg1.style.removeProperty('display');
        }
        if (uploadBtn1) {
          uploadBtn1.classList.remove('has-image', 'uploaded');
          const icon1 = uploadBtn1.querySelector('.ai-upload-icon');
          const text1 = uploadBtn1.querySelector('.ai-upload-text');
          if (icon1) icon1.style.removeProperty('display');
          if (text1) text1.style.removeProperty('display');
        }
        if (statusDiv1) {
          statusDiv1.style.display = 'none';
          statusDiv1.className = 'ai-upload-status';
          statusDiv1.textContent = '';
        }

        // Clear second upload preview
        const previewImg2 = context.querySelector('#ai-upload-preview-2');
        const uploadBtn2 = context.querySelector('#ai-upload-trigger-2');
        const statusDiv2 = context.querySelector('#ai-upload-status-2');

        if (previewImg2) {
          previewImg2.src = '';
          previewImg2.style.removeProperty('display');
        }
        if (uploadBtn2) {
          uploadBtn2.classList.remove('has-image', 'uploaded');
          const icon2 = uploadBtn2.querySelector('.ai-upload-icon');
          const text2 = uploadBtn2.querySelector('.ai-upload-text');
          if (icon2) icon2.style.removeProperty('display');
          if (text2) text2.style.removeProperty('display');
        }
        if (statusDiv2) {
          statusDiv2.style.display = 'none';
          statusDiv2.className = 'ai-upload-status';
          statusDiv2.textContent = '';
        }

        // Clear generated images grid
        const imagesGrid = context.querySelector('.ai-generated-images-grid');
        if (imagesGrid) {
          imagesGrid.innerHTML = '';
        }

        // Clear file inputs
        const fileInputs = context.querySelectorAll('.ai-upload-input, #ai-file-input, #ai-file-input-2');
        fileInputs.forEach(input => {
          input.value = '';
        });
      };

      // Clear in drawer if exists
      const drawer = this.activeDrawer || document.querySelector('quick-buy-drawer[open]');
      if (drawer) {
        clearInContext(drawer, 'drawer');
      }

      // Clear in popover if exists
      const popover = this.activePopover || document.querySelector('quick-buy-popover[open]');
      if (popover) {
        clearInContext(popover, 'popover');
      }

      // Always clear in main page as well (in case user added from main page)
      clearInContext(document, 'main page');

      // Clear FilePond instances globally
      if (window.FilePond) {
        const ponds = document.querySelectorAll('.filepond');
        ponds.forEach(pond => {
          const instance = FilePond.find(pond);
          if (instance) {
            instance.removeFiles();
          }
        });
      }

      // Dispatch event for any custom handlers
      document.dispatchEvent(new CustomEvent('upsell:images-cleared'));

    }

    goToCheckout() {
      // Create a form to submit to checkout (Shopify's standard method)
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = window.themeVariables?.routes?.cartUrl || '/cart';

      // Add the checkout input
      const checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = '';
      form.appendChild(checkoutInput);

      // Append to body and submit
      document.body.appendChild(form);
      form.submit();
    }
  }

  // Initialize when DOM is ready
  function initUpsellPopup() {
    window.upsellPopup = new UpsellPopup();
    window.upsellPopup.initFormInterception();
    window.upsellPopup.observeDynamicForms();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initUpsellPopup);
  } else {
    initUpsellPopup();
  }
})();
